/* eslint-disable @typescript-eslint/no-magic-numbers */
import { DecimalPipe } from '@angular/common';
import { DestroyRef, Injectable, NgZone, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as d3 from 'd3';
import { Subscription, filter, merge, startWith } from 'rxjs';

import { fromSvgEvent$ } from '@shared/helpers/graph/svg-event.helper';
import { runOutsideAngularZone } from '@shared/operators/run-outside-angular-zone.operator';
import { D3ChartSvg, D3ChartSvgg, D3CursorEvent, D3XScale, D3YScale } from '@shared/types/common-graph.types';
import { ValueKeys } from '@shared/types/value-keys';
import { isDefined } from '@shared/utils/is-defined';
import { getRuntimeUniqueId } from '@shared/utils/runtime-unique-id';

import { ChartType, ChartTypeConfig, ChartXAxisConfig, TooltipConfig } from '../chart.view-model';
import { getBarData } from '../helpers/stacked-bar.helper';
import { getClosestIndexToCursor } from '../helpers/x-scale.helper';

interface TooltipWithType<Data extends object> {
  tooltip: D3ChartSvgg;
  data: Data[];
  xAxisConfig: ChartXAxisConfig<Data>;
  dataKey?: ValueKeys<Data, number | null | undefined>;
  dataMapper?: (data: Data, idx: number) => number | null;
  tooltipConfig: TooltipConfig<Data>;
  yScale: D3YScale;
  boxYCalcFn?: StackedBarBoxYCalcFn<Data>;
}

type StackedBarBoxYCalcFn<Data extends object> = (data: Data) => number;

interface TooltipGroup {
  tooltipData: {
    tooltip: D3ChartSvgg;
    height: number;
  }[];
  translateX: number;
  translateY: number;
  height: number;
}

export interface ChartData<Data extends object> {
  chart: ChartTypeConfig<Data>;
  yScale: D3YScale;
}

export interface CursorTooltipRenderer<Data extends object> {
  show(data: Data, index: number, x: number): number;
  hide(): void;
}

@Injectable()
export class LineTooltipService<Data extends object> {
  readonly #ngZone = inject(NgZone);
  readonly #destroyRef = inject(DestroyRef);
  readonly #decimalPipe = inject(DecimalPipe);

  private tooltipForLinesEventListenerSubscription?: Subscription;

  private readonly LINE_TOOLTIP_CONFIG = {
    BORDER_RADIUS: 4,
    HORIZONTAL_PADDING: 8,
    VERTICAL_PADDING: 4,
    INITIAL_TOP_OFFSET: 13,
  };

  private readonly MIN_TRANSLATE_Y = -15;
  private readonly TOOLTIP_GAP = 2;

  private lastEvent?: D3CursorEvent;

  public resetListeners(): void {
    this.tooltipForLinesEventListenerSubscription?.unsubscribe();
  }

  public generateTooltipsForLines(
    svg: D3ChartSvg,
    linesContainer: D3ChartSvgg,
    chartWidth: number,
    chartHeight: number,
    xScale: D3XScale,
    xAxisConfig: ChartXAxisConfig<Data>,
    charts: ChartData<Data>[],
    data: Data[],
    cursorTooltip?: CursorTooltipRenderer<Data>
  ): void {
    const uniqueEventListenerId = `chart-id${getRuntimeUniqueId()}`;

    const tooltips: TooltipWithType<Data>[] = [];

    charts.forEach(({ chart, yScale }) => {
      switch (chart.type) {
        case ChartType.StackedBar: {
          chart.bars.forEach((bar, idx) => {
            tooltips.push({
              tooltip: this.createTooltip(linesContainer),
              data,
              xAxisConfig,
              dataKey: bar.dataKey,
              tooltipConfig: bar.tooltipConfig,
              yScale,
              boxYCalcFn: (data) => {
                const { barHeights, barYs } = getBarData(data, chart.bars, yScale);
                const barHeight = barHeights[idx];
                const barY = barYs[idx];

                return barY + barHeight / 2;
              },
            });
          });
          break;
        }
        case ChartType.GroupedBar: {
          chart.bars.forEach((bar) => {
            tooltips.push({
              tooltip: this.createTooltip(linesContainer),
              data,
              xAxisConfig,
              dataKey: bar.dataKey,
              dataMapper: bar.dataMapper,
              tooltipConfig: bar.tooltipConfig,
              yScale,
            });
          });
          break;
        }
        case ChartType.BackgroundRect: {
          break;
        }
        case ChartType.Line: {
          tooltips.push({
            tooltip: this.createTooltip(linesContainer),
            data,
            xAxisConfig,
            dataKey: chart.dataKey,
            dataMapper: chart.dataMapper,
            tooltipConfig: chart.tooltipConfig,
            yScale,
          });
          break;
        }
        default: {
          tooltips.push({
            tooltip: this.createTooltip(linesContainer),
            data,
            xAxisConfig,
            dataKey: chart.dataKey,
            tooltipConfig: chart.tooltipConfig,
            yScale,
          });
        }
      }
    });

    this.tooltipForLinesEventListenerSubscription?.unsubscribe();
    this.tooltipForLinesEventListenerSubscription = new Subscription();

    const desktopStartEvent$ = fromSvgEvent$(
      svg,
      `mouseenter.${uniqueEventListenerId} mousemove.${uniqueEventListenerId} click.${uniqueEventListenerId}`
    );

    const mobileStartEvent$ = fromSvgEvent$(svg, `touchstart.${uniqueEventListenerId} touchmove.${uniqueEventListenerId}`);

    this.tooltipForLinesEventListenerSubscription.add(
      merge(desktopStartEvent$, mobileStartEvent$)
        .pipe(
          startWith(this.lastEvent),
          filter((e): e is D3CursorEvent => !!e),
          runOutsideAngularZone(this.#ngZone),
          takeUntilDestroyed(this.#destroyRef)
        )
        .subscribe((event) => {
          this.lastEvent = event;
          this.displayLineTooltipsOnCursorEvent(
            event,
            xScale,
            chartWidth,
            chartHeight,
            linesContainer,
            xAxisConfig,
            tooltips,
            cursorTooltip
          );
        })
    );

    const desktopEndEvent$ = fromSvgEvent$(svg, `mouseleave.${uniqueEventListenerId}`);

    const mobileEndEvent$ = fromSvgEvent$(svg, `touchend.${uniqueEventListenerId} touchcancel.${uniqueEventListenerId}`);

    this.tooltipForLinesEventListenerSubscription.add(
      merge(desktopEndEvent$, mobileEndEvent$)
        .pipe(runOutsideAngularZone(this.#ngZone), takeUntilDestroyed(this.#destroyRef))
        .subscribe(() => {
          this.lastEvent = undefined;
          this.hideLineTooltips(tooltips);
          cursorTooltip?.hide();
        })
    );
  }

  private createTooltip(linesContainer: D3ChartSvgg): D3ChartSvgg {
    return linesContainer
      .append('g')
      .style('pointer-events', 'none')
      .style('display', 'none')
      .style('visibility', 'hidden')
      .style('z-index', 1)
      .call((g) => g.append('rect').attr('rx', this.LINE_TOOLTIP_CONFIG.BORDER_RADIUS).attr('ry', this.LINE_TOOLTIP_CONFIG.BORDER_RADIUS))
      .call((g) =>
        g
          .append('text')
          .attr('class', `text-xs`)
          .call((text) =>
            text
              .attr('x', this.LINE_TOOLTIP_CONFIG.HORIZONTAL_PADDING)
              .attr('y', this.LINE_TOOLTIP_CONFIG.INITIAL_TOP_OFFSET + this.LINE_TOOLTIP_CONFIG.VERTICAL_PADDING)
              .text('')
          )
      );
  }

  private displayLineTooltipsOnCursorEvent(
    event: D3CursorEvent,
    xScale: D3XScale,
    chartWidth: number,
    chartHeight: number,
    linesContainer: D3ChartSvgg,
    xAxisConfig: ChartXAxisConfig<Data>,
    tooltips: TooltipWithType<Data>[],
    cursorTooltip?: CursorTooltipRenderer<Data>
  ): void {
    const cursorDataIndex = getClosestIndexToCursor(event, tooltips[0]?.data ?? [], xAxisConfig.dataKey, xScale, linesContainer);
    let cursorTooltipHeight = 0;
    if (cursorDataIndex !== null) {
      const cursorData = tooltips[0]?.data[cursorDataIndex];
      if (cursorData) {
        const cursorX = Math.min(Math.max(d3.pointer(event, linesContainer.node())[0], 0), chartWidth);
        cursorTooltipHeight = cursorTooltip?.show(cursorData, cursorDataIndex, cursorX) ?? 0;
      }
    }
    const tooltipGroups = tooltips.map(
      ({ tooltip, data, dataKey, dataMapper, tooltipConfig, xAxisConfig, yScale, boxYCalcFn }): TooltipGroup | undefined => {
        const closestIndex = getClosestIndexToCursor(event, data, xAxisConfig.dataKey, xScale, linesContainer)!;

        return this.setInitialPositionsAndCreateTooltipGroupFromTooltip(
          tooltip,
          linesContainer,
          data[closestIndex],
          closestIndex,
          dataKey,
          dataMapper,
          tooltipConfig,
          boxYCalcFn,
          chartWidth,
          yScale,
          event
        );
      }
    );

    const nonNullTooltipGroups = tooltipGroups.filter((tooltipGroup) => !!tooltipGroup);

    this.sortTooltipGroups(nonNullTooltipGroups);

    this.shiftTooltipGroupsTranslateYValuesIfNeeded(nonNullTooltipGroups, chartHeight, cursorTooltipHeight);
    this.setTranslatePropertyOfTooltipGroups(nonNullTooltipGroups);
  }

  private setInitialPositionsAndCreateTooltipGroupFromTooltip(
    tooltip: D3ChartSvgg,
    linesContainer: D3ChartSvgg,
    data: Data,
    dataIdx: number,
    dataKey: ValueKeys<Data, number | null | undefined> | undefined,
    dataMapper: ((data: Data, idx: number) => number | null) | undefined,
    tooltipConfig: TooltipConfig<Data>,
    boxYCalcFn: StackedBarBoxYCalcFn<Data> | undefined,
    chartWidth: number,
    yScale: D3YScale,
    event: D3CursorEvent
  ): TooltipGroup | undefined {
    const textSelection = tooltip.select('text');
    const rectSelection = tooltip.select('rect');

    const valueToDisplay = dataKey ? (data[dataKey] as number | undefined) : dataMapper!(data, dataIdx);
    const hidden = tooltipConfig.hidden ? tooltipConfig.hidden(data, dataIdx) : false;

    textSelection.text(
      isDefined(valueToDisplay) && !hidden ? (tooltipConfig.formatter?.(valueToDisplay) ?? this.#decimalPipe.transform(valueToDisplay)) : ''
    );

    if (!isDefined(valueToDisplay) || hidden) {
      tooltip.style('display', 'none');
      tooltip.style('visibility', 'hidden');

      return undefined;
    }

    tooltip.style('display', null);
    tooltip.style('visibility', null);

    this.applyTooltipColors(textSelection, rectSelection, tooltipConfig, data, dataIdx);

    tooltip.style('visibility', null);

    const textBox = (tooltip.select('text').node() as SVGGElement).getBBox();
    rectSelection
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', textBox.width + this.LINE_TOOLTIP_CONFIG.HORIZONTAL_PADDING * 2)
      .attr('height', textBox.height + this.LINE_TOOLTIP_CONFIG.VERTICAL_PADDING * 2);

    const boundingBox = (tooltip.node() as SVGGElement).getBBox();

    const tooltipXOffset = 8;

    let translateX = d3.pointer(event, linesContainer.node())[0];

    if (translateX < 0) {
      translateX = 0;
    }
    if (translateX > chartWidth) {
      translateX = chartWidth;
    }

    const middleX = chartWidth / 2;

    const tooltipTranslateX = translateX < middleX ? translateX + tooltipXOffset : translateX - boundingBox.width - tooltipXOffset;

    const tooltipTranslateY = (boxYCalcFn ? boxYCalcFn(data) : yScale(valueToDisplay)) - boundingBox.height / 2;

    return {
      tooltipData: [
        {
          height: boundingBox.height,
          tooltip,
        },
      ],
      height: boundingBox.height,
      translateX: tooltipTranslateX,
      translateY: tooltipTranslateY,
    };
  }

  // Applies the tooltip's text and background colours. The background can be a Tailwind class
  // (`fillColorClass`) or an explicit hex (`fillColor`, takes precedence); the latter clears the
  // inline fill when absent so the class-based background applies.
  private applyTooltipColors(
    textSelection: d3.Selection<d3.BaseType, unknown, null, undefined>,
    rectSelection: d3.Selection<d3.BaseType, unknown, null, undefined>,
    tooltipConfig: TooltipConfig<Data>,
    data: Data,
    dataIdx: number
  ): void {
    const bgClass =
      typeof tooltipConfig.fillColorClass === 'function' ? tooltipConfig.fillColorClass(data, dataIdx) : tooltipConfig.fillColorClass;
    const bgColor = typeof tooltipConfig.fillColor === 'function' ? tooltipConfig.fillColor(data, dataIdx) : tooltipConfig.fillColor;
    const textClass =
      typeof tooltipConfig.textColorClass === 'function' ? tooltipConfig.textColorClass(data, dataIdx) : tooltipConfig.textColorClass;

    textSelection.attr('class', `text-xs ${textClass}`);
    rectSelection.attr(
      'class',
      `${bgClass ?? ''} drop-shadow-foreground-primary-100-shadow-30 dark:drop-shadow-foreground-secondary-100-shadow-30`
    );
    rectSelection.style('fill', () => bgColor ?? null);
  }

  private sortTooltipGroups(tooltipGroups: TooltipGroup[]): void {
    tooltipGroups.sort((a, b) => a.translateY - b.translateY);
  }

  private shiftTooltipGroupsTranslateYValuesIfNeeded(
    tooltipGroups: TooltipGroup[],
    chartHeight: number,
    cursorTooltipHeight: number
  ): void {
    let somethingChanged = false;

    for (const group of tooltipGroups) {
      somethingChanged = this.adjustGroupIfItFlowsOutOfChartUpwards(group, cursorTooltipHeight);
    }

    for (let i = 0; i < tooltipGroups.length && !somethingChanged; i++) {
      const group = tooltipGroups[i];
      const nextGroup = tooltipGroups[i + 1];

      somethingChanged = nextGroup && this.mergeGroupsIfTheyOverlap(group, nextGroup, tooltipGroups, i);
    }

    for (let i = tooltipGroups.length - 1; i >= 0 && !somethingChanged; i--) {
      const group = tooltipGroups[i];

      somethingChanged = this.adjustGroupIfItFlowsOutOfChartDownwards(group, chartHeight);
    }

    if (somethingChanged) {
      this.sortTooltipGroups(tooltipGroups);
      this.shiftTooltipGroupsTranslateYValuesIfNeeded(tooltipGroups, chartHeight, cursorTooltipHeight);
    }
  }

  private adjustGroupIfItFlowsOutOfChartUpwards(group: TooltipGroup, cursorTooltipHeight: number): boolean {
    const minTranslateY = Math.max(this.MIN_TRANSLATE_Y, cursorTooltipHeight + this.TOOLTIP_GAP);
    if (group.translateY < minTranslateY) {
      group.translateY = minTranslateY;
      return true;
    }

    return false;
  }

  private adjustGroupIfItFlowsOutOfChartDownwards(group: TooltipGroup, chartHeight: number): boolean {
    if (group.translateY + group.height > chartHeight) {
      group.translateY = chartHeight - group.height;
      return true;
    }

    return false;
  }

  private mergeGroupsIfTheyOverlap(
    group: TooltipGroup,
    nextGroup: TooltipGroup,
    tooltipGroups: TooltipGroup[],
    groupIndex: number
  ): boolean {
    if (this.areTooltipGroupsOverlapped(group, nextGroup)) {
      group.tooltipData.push(...nextGroup.tooltipData);

      const oldBottom = Math.max(group.translateY + group.height, nextGroup.translateY + nextGroup.height);
      const oldCombinedHeight = oldBottom - group.translateY;

      group.height += nextGroup.height + this.TOOLTIP_GAP;

      const shiftAmount = (group.height - oldCombinedHeight) / 2;
      group.translateY -= shiftAmount;
      tooltipGroups.splice(groupIndex + 1, 1);
      return true;
    }

    return false;
  }

  private areTooltipGroupsOverlapped(group1: TooltipGroup, group2: TooltipGroup): boolean {
    return group1.translateY + group1.height + this.TOOLTIP_GAP >= group2.translateY;
  }

  private setTranslatePropertyOfTooltipGroups(tooltipGroups: TooltipGroup[]): void {
    tooltipGroups.forEach((tooltipGroup) => {
      let currentShift = 0;
      tooltipGroup.tooltipData.forEach((tooltipData) => {
        tooltipData.tooltip.attr('transform', `translate(${tooltipGroup.translateX},${tooltipGroup.translateY + currentShift})`);
        currentShift += tooltipData.height + this.TOOLTIP_GAP;
      });
    });
  }

  private hideLineTooltips(tooltips: TooltipWithType<Data>[]): void {
    tooltips.forEach(({ tooltip }) => {
      tooltip.style('display', 'none');
      tooltip.style('visibility', 'hidden');
    });
  }
}
